/**
 * PTEL Visor - GlossaryTooltip Component
 * Tooltip accesible que muestra definiciones del glosario técnico
 *
 * V04 - Medidas de accesibilidad:
 * - Hover/focus muestra tooltip
 * - Navegable por teclado (Tab)
 * - ARIA: role="tooltip", aria-describedby
 * - Escape cierra tooltip
 *
 * @see src/constants/glossary.ts - Glosario de términos
 */

import React, { memo, useId } from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from './tooltip';
import { getGlossaryEntry, type GlossaryEntry as _GlossaryEntry } from '../../constants/glossary';
import { cn } from '../../lib/utils';

// =============================================================================
// TIPOS
// =============================================================================

export interface GlossaryTooltipProps {
  /** Término técnico a buscar en el glosario */
  term: string;
  /** Contenido a mostrar (si no se proporciona, usa el label del glosario) */
  children?: React.ReactNode;
  /** Clase CSS adicional para el trigger */
  className?: string;
  /** Mostrar ejemplo en el tooltip */
  showExample?: boolean;
  /** Lado preferido para mostrar el tooltip */
  side?: 'top' | 'right' | 'bottom' | 'left';
  /** Deshabilitar el tooltip (muestra solo el contenido) */
  disabled?: boolean;
}

// =============================================================================
// ESTILOS
// =============================================================================

const triggerStyles = cn(
  'cursor-help',
  'border-b border-dashed border-current',
  'hover:border-solid hover:text-lime-600',
  'focus:outline-none focus:ring-2 focus:ring-lime-500 focus:ring-offset-1',
  'transition-colors duration-150'
);

const contentStyles = cn(
  'max-w-xs sm:max-w-sm',
  'bg-zinc-900 text-zinc-100',
  'border border-zinc-700',
  'shadow-lg'
);

// =============================================================================
// COMPONENTE PRINCIPAL
// =============================================================================

/**
 * Tooltip que muestra la definición de un término técnico del glosario PTEL.
 *
 * @example
 * ```tsx
 * // Uso básico - muestra el label del glosario
 * <GlossaryTooltip term="utm" />
 *
 * // Con contenido personalizado
 * <GlossaryTooltip term="epsg">
 *   código EPSG:25830
 * </GlossaryTooltip>
 *
 * // Con ejemplo incluido
 * <GlossaryTooltip term="geocodificacion" showExample />
 * ```
 */
export const GlossaryTooltip = memo<GlossaryTooltipProps>(function GlossaryTooltip({
  term,
  children,
  className,
  showExample = false,
  side = 'top',
  disabled = false,
}) {
  const tooltipId = useId();
  const entry = getGlossaryEntry(term);

  // Si no existe el término, renderizar solo el contenido sin tooltip
  if (!entry) {
    console.warn(`[GlossaryTooltip] Término no encontrado: "${term}"`);
    return <span className={className}>{children ?? term}</span>;
  }

  // Si está deshabilitado, renderizar solo el contenido
  if (disabled) {
    return <span className={className}>{children ?? entry.label}</span>;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={cn(triggerStyles, className)}
          tabIndex={0}
          aria-describedby={tooltipId}
        >
          {children ?? entry.label}
        </span>
      </TooltipTrigger>
      <TooltipContent
        id={tooltipId}
        role="tooltip"
        side={side}
        className={contentStyles}
        sideOffset={4}
      >
        <div className="space-y-1.5">
          {/* Título */}
          <p className="font-semibold text-lime-400 text-sm">
            {entry.label}
          </p>
          
          {/* Descripción */}
          <p className="text-zinc-300 text-xs leading-relaxed">
            {entry.description}
          </p>
          
          {/* Ejemplo (opcional) */}
          {showExample && entry.example && (
            <p className="text-zinc-400 text-xs italic border-t border-zinc-700 pt-1.5 mt-1.5">
              Ej: {entry.example}
            </p>
          )}
        </div>
      </TooltipContent>
    </Tooltip>
  );
});

// =============================================================================
// COMPONENTE AUXILIAR: GlossaryTerm
// =============================================================================

export interface GlossaryTermProps {
  /** Término técnico */
  term: string;
  /** Clase CSS adicional */
  className?: string;
}

/**
 * Versión simplificada que solo subraya el término sin tooltip.
 * Útil para marcar términos técnicos visualmente.
 */
export const GlossaryTerm = memo<GlossaryTermProps>(function GlossaryTerm({
  term,
  className,
}) {
  const entry = getGlossaryEntry(term);
  
  return (
    <span
      className={cn(
        'border-b border-dotted border-zinc-500',
        className
      )}
      title={entry?.description}
    >
      {entry?.label ?? term}
    </span>
  );
});

// =============================================================================
// UTILIDAD: Wrapper para texto con términos
// =============================================================================

/**
 * Encuentra y envuelve términos del glosario en un texto.
 * Útil para procesar bloques de texto automáticamente.
 *
 * @param text - Texto a procesar
 * @param terms - Lista de términos a buscar
 * @returns Array de elementos React con tooltips
 *
 * @example
 * ```tsx
 * const elements = wrapGlossaryTerms(
 *   "Las coordenadas UTM usan el sistema EPSG:25830",
 *   ['utm', 'epsg']
 * );
 * ```
 */
export function wrapGlossaryTerms(
  text: string,
  terms: string[]
): React.ReactNode[] {
  if (!terms.length) return [text];

  // Crear regex para encontrar términos (case-insensitive, palabras completas)
  const pattern = new RegExp(
    `\\b(${terms.map(t => {
      const entry = getGlossaryEntry(t);
      return entry ? `${entry.label}|${entry.term}` : t;
    }).join('|')})\\b`,
    'gi'
  );

  const parts = text.split(pattern);
  
  return parts.map((part, index) => {
    // Buscar si esta parte coincide con algún término
    const matchingTerm = terms.find(t => {
      const entry = getGlossaryEntry(t);
      if (!entry) return false;
      const lowerPart = part.toLowerCase();
      return lowerPart === entry.label.toLowerCase() || lowerPart === entry.term;
    });

    if (matchingTerm) {
      return (
        <GlossaryTooltip key={`${matchingTerm}-${index}`} term={matchingTerm}>
          {part}
        </GlossaryTooltip>
      );
    }

    return part;
  });
}

export default GlossaryTooltip;
